import { useState, useMemo, useRef, useCallback } from "react";
import { bool, number, string, object, shape, arrayOf, oneOfType } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { downloadFile, flattenProjectUsers, formatFileName, TEAM_ROLES } from "@utils";
import { useActiveProjectStore, useActiveProjectsWebsocket } from "@hooks";
import {
  removeProjectObserverQuery,
  addProjectObserverQuery,
  setProjectOwnerQuery,
  removeProjectAttachmentQuery,
  removeUserFromWorkTypeQuery,
  removeProjectContractorQuery,
  inviteContractorQuery,
  getProjectDetailsQuery,
} from "@query";
import {
  AddButton,
  Alert,
  AsyncPicker,
  AvatarList,
  ClearButton,
  InfoLabel,
  TagList,
  UserItem,
} from "@components";
import { InviteContractorDialog } from "@dialogs";
import Grid from "@material-ui/core/Grid";
import { AttachmentsEditor } from "../index";
import useStyle from "./ActionPanel.style.js";

export const ActionPanel = observer(({
  uuid,
  isOwner,
  isContractor,
  isMini,
  users,
  owner,
  author,
  teamMembers,
  workTypes,
  attachments,
  sticky,
  contractors: initialContractors,
}) => {
  const { t } = useTranslation();
  const activeProjects = useActiveProjectStore();
  const socket = useActiveProjectsWebsocket();
  const classes = useStyle();

  const memberSelectAnchorRef = useRef(null);
  const ownerAnchorRef = useRef(null);
  const [memberSelectVisible, showMemberSelect] = useState(false);
  const [ownerPickerVisible, showOwnerPicker] = useState(false);
  const [contractorDialogVisible, showContractorDialog] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [contractors, setContractors] = useState(initialContractors || []);

  const canEdit = isOwner && !isContractor;

  const safeUsers = useMemo(() => (users || []).filter(Boolean), [users]);

  const allUsers = useMemo(
    () =>
      author || owner
        ? flattenProjectUsers({
            author: author || null,
            owner: owner || author || null,
            observer: safeUsers,
          })
        : safeUsers,
    [author, owner, safeUsers]
  );

  const nonContractorMembers = useMemo(
    () => (teamMembers || []).filter(
      (m) => m && m.teamRoleId !== TEAM_ROLES.EXTERNAL_CONTRACTOR
    ),
    [teamMembers]
  );

  const contractorOnlyMembers = useMemo(
    () => (teamMembers || []).filter(
      (m) => m && m.teamRoleId === TEAM_ROLES.EXTERNAL_CONTRACTOR
    ),
    [teamMembers]
  );

  const setOwner = useCallback(
    async (newOwner) => {
      try {
        const observer = users.filter((u) => u.email !== newOwner.email);
        if (owner && owner.id !== author?.id)
          observer.push({ ...owner });
        await setProjectOwnerQuery(uuid, newOwner.email);
        socket?.requestProjectUserPromote(uuid, newOwner.uuid);
        activeProjects.editLocalProjectData(uuid, "owner", newOwner);
        activeProjects.editLocalProjectData(uuid, "observers", observer);
        showOwnerPicker(false);
      } catch (e) {
        setAlertText(e?.response?.data?.error || t("common.error"));
      }
    },
    [uuid, users, owner, author, socket, activeProjects, t]
  );

  const handleMemberAdd = useCallback(
    async (v) => {
      try {
        await addProjectObserverQuery(uuid, v.email);
        socket?.requestProjectUserAdd(uuid, v);
        activeProjects.editLocalProjectData(uuid, "observers", [...users, v]);
      } catch (e) {
        setAlertText(e?.response?.data?.error || t("common.error"));
      }
    },
    [uuid, users, socket, activeProjects, t]
  );

  const handleMemberRemove = useCallback(
    async (userId) => {
      try {
        const user = users.find((u) => u.id === userId);
        const email = user?.email;

        await removeProjectObserverQuery(uuid, email);
        socket?.requestProjectUserRemove(uuid, user.uuid);
        const observer = users.filter((u) => u.id !== userId);
        if (owner && email === owner.email) {
          const newOwner = author;
          observer.push(owner);
          await setProjectOwnerQuery(uuid, newOwner.email);
          activeProjects.editLocalProjectData(uuid, "observers", observer);
          activeProjects.editLocalProjectData(uuid, "owner", newOwner);
        } else {
          activeProjects.editLocalProjectData(uuid, "observers", observer);
          workTypes.forEach(wt => {
            if (wt.estimatorUuid === user.uuid)
              removeUserFromWorkTypeQuery(uuid, wt.id, user.uuid);
          });
        }
      } catch (e) {
        setAlertText(e?.response?.data?.error || t("common.error"));
      }
    },
    [uuid, users, owner, author, workTypes, socket, activeProjects, t]
  );

  const handleFileRemove = useCallback(
    async (fileId) => {
      try {
        const attachment = attachments.find((f) => f.id === fileId);
        if (!attachment) return;
        await removeProjectAttachmentQuery(
          uuid,
          attachment.name,
          attachment.uuid
        );
        activeProjects.editLocalProjectData(
          uuid,
          "attachmentRemove",
          attachment.uuid
        );
      } catch (e) {
        setAlertText(e?.response?.data?.error || t("common.error"));
      }
    },
    [uuid, attachments, activeProjects, t]
  );

  const handleFileDownload = useCallback(
    async (e, fileId) => {
      try {
        const attachment = attachments.find((f) => f.id === fileId);
        if (!attachment) return;
        await downloadFile(attachment.name, undefined, attachment.path);
      } catch (e) {
        setAlertText(e?.response?.data?.error || t("common.error"));
      }
    },
    [attachments, t]
  );

  const handleInviteContractor = useCallback(
    async (email, projectUuid) => {
      try {
        const result = await inviteContractorQuery(email, [projectUuid]);

        if (!result.hasError) {
          // Przeładuj dane projektu żeby odświeżyć listę kontrahentów
          try {
            const details = await getProjectDetailsQuery(projectUuid);
            activeProjects.updateLocalProject(projectUuid, details);
            if (details?.users?.contractor || details?.users?.contractors) {
              setContractors(details.users.contractor || details.users.contractors);
            } else if (result.user) {
              setContractors((prev) => {
                const exists = prev.some((c) => c.email === result.user.email || c.email === email);
                return exists ? prev : [...prev, result.user];
              });
            }
          } catch (e) {
            console.warn("[ActionPanel] Failed to reload project details after invite:", e?.message);
            if (result.user) {
              setContractors((prev) => [...prev, result.user]);
            }
          }
        }

        return result;
      } catch (e) {
        return {
          hasError: true,
          errorMessage: e?.response?.data?.error || e?.message,
        };
      }
    },
    [activeProjects]
  );

  const toggleMemberSelect = useCallback(() => showMemberSelect(v => !v), []);
  const closeMemberSelect = useCallback(() => showMemberSelect(false), []);
  const closeOwnerPicker = useCallback(() => showOwnerPicker(false), []);
  const toggleOwnerPicker = useCallback(() => showOwnerPicker(v => !v), []);
  const openContractorDialog = useCallback(() => showContractorDialog(true), []);
  const closeContractorDialog = useCallback(() => showContractorDialog(false), []);
  const closeAlert = useCallback(() => setAlertText(""), []);

  const handleContractorChange = useCallback(async () => {
    try {
      const details = await getProjectDetailsQuery(uuid);
      activeProjects.updateLocalProject(uuid, details);
      if (details?.users?.contractor || details?.users?.contractors) {
        setContractors(details.users.contractor || details.users.contractors);
      }
    } catch (e) {
      console.warn("[ActionPanel] Failed to reload contractors:", e?.message);
    }
  }, [uuid, activeProjects]);

  const handleContractorRemove = useCallback(async (contractorId) => {
    const contractor = contractors.find((c) => c.id === contractorId);
    if (!contractor?.email) return;
    try {
      await removeProjectContractorQuery(uuid, contractor.email);
      setContractors((prev) => prev.filter((c) => c.id !== contractorId));
      activeProjects.editLocalProjectData(uuid, "contractorRemove", contractorId);
    } catch (e) {
      setAlertText(e?.response?.data?.error || t("common.error"));
    }
  }, [uuid, contractors, activeProjects, t]);

  return (
    <Grid item container className={sticky ? classes.sticky : undefined}>
      {!isContractor && (
        <>
          <InfoLabel
            label="views.active.popup.members"
            spacing={32}
            action={
              canEdit ? (
                <ClearButton
                  size="small"
                  ref={ownerAnchorRef}
                  onClick={toggleOwnerPicker}
                >
                  {owner
                    ? t("views.project.change_owner")
                    : t("views.project.add_owner")}
                </ClearButton>
              ) : undefined
            }
          />
          <Grid item container id="projectTeamMembers">
            <AvatarList
              users={allUsers}
              removeHandler={canEdit ? handleMemberRemove : undefined}
              size={32}
            />
            {canEdit && (
              <>
                <AddButton
                  onClick={toggleMemberSelect}
                  ref={memberSelectAnchorRef}
                  style={{ width: 32, height: 32 }}
                  className="ml-4"
                />
                <AsyncPicker
                  inputName="members-search"
                  selected={allUsers}
                  isOpen={memberSelectVisible}
                  anchorEl={memberSelectAnchorRef.current}
                  disableOptions={(o) => !o || o.status === 0 || o.id === author?.id}
                  labelKey="fullname"
                  inputLabel={t("views.active.search_member")}
                  ContentItem={UserItem}
                  noItemsText={t("views.active.popup.no_members")}
                  title={t("views.active.popup.members")}
                  options={nonContractorMembers}
                  onClickAway={closeMemberSelect}
                  onAdd={handleMemberAdd}
                  onRemove={handleMemberRemove}
                />
                <AsyncPicker
                  inputName="owner-picker"
                  selected={[owner].filter(Boolean)}
                  isOpen={ownerPickerVisible}
                  anchorEl={ownerAnchorRef.current}
                  labelKey="fullname"
                  disableOptions={(o) => !o || o.status === 0 || o.id === owner?.id}
                  inputLabel={t("views.active.search_member")}
                  ContentItem={UserItem}
                  noItemsText={t("views.active.popup.no_members")}
                  title={t("views.active.popup.members")}
                  options={nonContractorMembers}
                  onClickAway={closeOwnerPicker}
                  onAdd={setOwner}
                  idKey="email"
                />
              </>
            )}
          </Grid>
        </>
      )}

      <InfoLabel
        label="views.active.popup.contractors"
        spacing={32}
      />
      <Grid item container>
        {contractors.length > 0 && (
          <AvatarList
            users={contractors}
            removeHandler={canEdit ? handleContractorRemove : undefined}
            size={32}
          />
        )}
        {canEdit && (
          <>
            <AddButton
              onClick={openContractorDialog}
              style={{ width: 32, height: 32 }}
              className={contractors.length > 0 ? "ml-4" : undefined}
            />
            <InviteContractorDialog
              isOpen={contractorDialogVisible}
              onClose={closeContractorDialog}
              onInvite={handleInviteContractor}
              projectUuid={uuid}
              contractorMembers={contractorOnlyMembers}
              alreadyInvited={contractors}
              onContractorChange={handleContractorChange}
            />
          </>
        )}
      </Grid>

      <InfoLabel
        label={`views.active.popup.${
          isMini ? "pdf_proposals" : "attachments"
        }`}
        spacing={32}
        action={
          canEdit && !isMini ? (
            <AttachmentsEditor
              attachments={attachments}
              uuid={uuid}
              multiple
            />
          ) : undefined
        }
      />
      <Grid item container>
        <TagList
          tags={attachments.map((a) => ({
            id: a.id,
            name: formatFileName(a.name),
          }))}
          onClick={handleFileDownload}
          onRemove={canEdit && !isMini ? handleFileRemove : undefined}
        />
      </Grid>

      <InfoLabel label="views.active.popup.tags" spacing={32} />
      <Grid item container>
        <TagList tags={workTypes} />
      </Grid>

      <Alert
        title={alertText}
        isOpen={!!alertText}
        onAccept={closeAlert}
        acceptText={t("common.close")}
      />
    </Grid>
  );
});

const userObj = shape({
  id: number,
  email: string,
  avatarColor: string,
  fullname: string,
  uuid: string,
  avatar: string,
  status: number,
});

ActionPanel.propTypes = {
  uuid: string.isRequired,
  isOwner: bool.isRequired,
  isContractor: bool,
  isMini: bool.isRequired,
  owner: userObj,
  author: userObj,
  users: arrayOf(userObj),
  teamMembers: arrayOf(userObj),
  contractors: arrayOf(userObj),
  attachments: arrayOf(object).isRequired,
  workTypes: arrayOf(
    shape({
      id: oneOfType([string, number]).isRequired,
      name: string.isRequired,
    })
  ).isRequired,
  sticky: bool,
};
